import request from './request'
import {
  normalizeTasksMapPeople,
  type TasksMapCategoryKey,
  type TasksMapPanelTab,
  type TasksMapPerson,
  type TasksMapPoint,
} from './tasksMap'
import { getMockTasksMapBoardResponse, shouldUseTasksMapFallbackMock, shouldUseTasksMapMock } from './tasksMapMetaMock'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
  message?: string
}

/** POST `/api/admin/tasks-map/board` request body */
export interface TasksMapBoardParams {
  date: string
  tab: string
  selected_staff_id: number
}

/** `staff_list[].orders[]` per API doc */
export interface TasksMapBoardOrder {
  index?: number
  id?: number | string
  order_id?: string
  order_no?: string
  order_id_text?: string
  address?: string
  full_address?: string
  time_start?: string
  time_end?: string
  time_text?: string
  latitude?: number | string
  longitude?: number | string
}

/** `staff_list[]` per API doc */
export interface TasksMapBoardStaff {
  id?: number | string
  name?: string
  role?: string
  staff_no?: string
  avatar?: string
  initials?: string
  order_count?: number
  orders?: TasksMapBoardOrder[]
}

/** `map.staff_markers[]` / `map.order_markers[]` */
export interface TasksMapBoardMarker {
  staff_id?: number | string
  name?: string
  initials?: string
  latitude?: number | string
  longitude?: number | string
  index?: number
  order_id?: string
  order_no?: string
  address?: string
}

/** `map.driver_routes[]` per API doc */
export interface TasksMapBoardRoutePoint {
  index?: number
  order_id?: string
  order_no?: string
  latitude?: number | string
  longitude?: number | string
}

/** `data.map` per API doc */
export interface TasksMapBoardMapData {
  staff_markers?: TasksMapBoardMarker[]
  order_markers?: TasksMapBoardMarker[]
  driver_routes?: TasksMapBoardRoutePoint[]
  selected_route?: TasksMapBoardRoutePoint[]
}

/** `data` per API doc */
export interface TasksMapBoardData {
  date?: string
  tab?: string
  selected_staff_id?: number
  staff_list?: TasksMapBoardStaff[]
  map?: TasksMapBoardMapData
}

export interface TasksMapBoardView {
  date: string
  tab: string
  selectedStaffId: number
  people: TasksMapPerson[]
  selectedRoute: TasksMapPoint[]
}

export function isTasksMapApiSuccess(code: unknown): boolean {
  return code === 200 || code === '200'
}

export function readTasksMapApiMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return ''
  const row = payload as Record<string, unknown>
  const message = row.msg ?? row.message
  return typeof message === 'string' ? message.trim() : ''
}

function sanitizeJsonText(raw: string): string {
  return raw
    .replace(/^\uFEFF/, '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFD]/g, '')
}

function parseTasksMapBoardPayload(raw: string): ApiResponse<TasksMapBoardData> {
  const cleaned = sanitizeJsonText(raw.trim())
  if (!cleaned) {
    return { code: 500, msg: 'Empty response from tasks map board API.', data: {} as TasksMapBoardData }
  }

  try {
    return JSON.parse(cleaned) as ApiResponse<TasksMapBoardData>
  } catch {
    return {
      code: 500,
      msg: 'Invalid response from tasks map board API.',
      data: {} as TasksMapBoardData,
    }
  }
}

export function isTasksMapBoardSuccess(payload: unknown): payload is ApiResponse<TasksMapBoardData> {
  if (!payload || typeof payload !== 'object') return false
  const row = payload as ApiResponse<TasksMapBoardData>
  if (isTasksMapApiSuccess(row.code) && row.data) return true

  const data = row.data
  return Boolean(data && typeof data === 'object' && Array.isArray(data.staff_list))
}

function isTasksMapBoardRetryableError(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false
  const row = payload as Record<string, unknown>
  const code = row.code
  if (typeof code === 'number' && code >= 500) return true

  const message = readTasksMapApiMessage(payload).toLowerCase()
  return (
    message.includes('utf-8') ||
    message.includes('utf8') ||
    message.includes('incorrectly encoded') ||
    message.includes('internal server error') ||
    message.includes('invalid response')
  )
}

function buildTasksMapBoardFallbackDates(primaryDate: string): string[] {
  const candidates = [
    primaryDate.trim(),
    '2025-05-27',
    '2025-12-31',
  ]
  return [...new Set(candidates.filter(Boolean))]
}

async function requestTasksMapBoardOnce(
  params: TasksMapBoardParams,
): Promise<ApiResponse<TasksMapBoardData>> {
  const payload = JSON.stringify(params)

  if (import.meta.env.DEV) {
    console.info('[tasks-map] POST /api/admin/tasks-map/board payload:', payload)
  }

  return request.post<ApiResponse<TasksMapBoardData>>(
    '/api/admin/tasks-map/board',
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status >= 200 && status < 600,
      responseType: 'text',
      transformResponse: [(data: string) => parseTasksMapBoardPayload(data)],
    },
  )
}

export function readTasksMapRequestError(error: unknown): string {
  if (!error || typeof error !== 'object') return ''

  const direct = readTasksMapApiMessage(error)
  if (direct) return direct

  if ('response' in error) {
    const axiosError = error as { response?: { data?: unknown } }
    const fromResponse = readTasksMapApiMessage(axiosError.response?.data)
    if (fromResponse) return fromResponse

    const data = axiosError.response?.data
    if (typeof data === 'string' && data.trim()) {
      return readTasksMapApiMessage(parseTasksMapBoardPayload(data))
    }
  }

  if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
    const message = (error as { message: string }).message.trim()
    if (message && message !== 'Network Error') return message
  }

  return ''
}

function isValidMapPoint(point: TasksMapPoint | null | undefined): point is TasksMapPoint {
  if (!point) return false
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return false
  if (point.lat === 0 && point.lng === 0) return false
  return Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180
}

/** 后端未返回坐标时，按序号在默认中心附近散开，保证地图能看到人 */
const FALLBACK_MAP_CENTER = { lat: 43.6895, lng: -79.3924 } as const

function fallbackPositionForIndex(index: number): TasksMapPoint {
  const angle = ((index * 137.508) % 360) * (Math.PI / 180)
  const radius = 0.018 + (index % 3) * 0.006
  return {
    lat: FALLBACK_MAP_CENTER.lat + Math.cos(angle) * radius,
    lng: FALLBACK_MAP_CENTER.lng + Math.sin(angle) * radius,
  }
}

function readPointDeep(row: Record<string, unknown>): TasksMapPoint | null {
  const direct = readPoint(row)
  if (direct) return direct

  for (const key of ['location', 'geo', 'coordinate', 'coordinates', 'position', 'map', 'latlng']) {
    const nested = row[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const point = readPoint(nested as Record<string, unknown>)
      if (point) return point
    }
  }
  return null
}

function readString(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number') return String(value)
  }
  return ''
}

function readNumber(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return null
}

/** 兼容大小写差异的字段读取（后端偶发 Latitude / LAT 等） */
function readNumberLoose(row: Record<string, unknown>, ...keys: string[]): number | null {
  const direct = readNumber(row, ...keys)
  if (direct !== null) return direct

  const lowered = new Map<string, unknown>()
  for (const [key, value] of Object.entries(row)) {
    lowered.set(key.toLowerCase(), value)
  }
  for (const key of keys) {
    const value = lowered.get(key.toLowerCase())
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) return parsed
    }
  }
  return null
}

function parseLatLngPair(value: unknown): TasksMapPoint | null {
  if (typeof value !== 'string' || !value.trim()) return null
  const parts = value.split(/[,;\s]+/).map((part) => part.trim()).filter(Boolean)
  if (parts.length < 2) return null
  const lat = Number(parts[0])
  const lng = Number(parts[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

function readPoint(row: Record<string, unknown>): TasksMapPoint | null {
  const lat = readNumberLoose(row, 'latitude', 'lat', 'geo_lat', 'y')
  const lng = readNumberLoose(row, 'longitude', 'lng', 'lon', 'geo_lng', 'x')
  if (lat !== null && lng !== null) return { lat, lng }

  for (const key of ['latlng', 'lat_lng', 'geo', 'coordinate', 'coordinates']) {
    const paired = parseLatLngPair(row[key])
    if (paired) return paired
  }
  return null
}

function formatOrderTime(order: TasksMapBoardOrder): string {
  const timeText = order.time_text?.trim()
  if (timeText) return timeText

  const start = order.time_start?.trim() ?? ''
  const end = order.time_end?.trim() ?? ''
  if (start && end) return `${start} - ${end}`
  return start || end
}

function formatOrderNo(order: TasksMapBoardOrder): string {
  const text = order.order_id_text?.trim() || order.order_id?.trim()
  if (text) return text.replace(/^#/, '')
  return order.order_no?.trim() ?? ''
}

export function toTasksMapBoardTab(tab: TasksMapPanelTab): string {
  return tab === 'inspectors' ? 'inspector' : 'technician'
}

export function buildTasksMapBoardParams(input: {
  date: string
  tab: TasksMapPanelTab
  selectedStaffId?: string | number
}): TasksMapBoardParams {
  let selectedStaffId = 0
  if (input.selectedStaffId !== undefined && input.selectedStaffId !== null && input.selectedStaffId !== '') {
    const parsed = Number(input.selectedStaffId)
    selectedStaffId = Number.isNaN(parsed) ? 0 : parsed
  }

  return {
    date: input.date.trim(),
    tab: toTasksMapBoardTab(input.tab),
    selected_staff_id: selectedStaffId,
  }
}

function mapBoardOrder(
  order: TasksMapBoardOrder,
  fallbackSequence: number,
): TasksMapPerson['orders'][number] | null {
  const row = order as Record<string, unknown>
  const id = readString(row, 'id', 'order_no', 'order_id') || `order-${fallbackSequence}`
  const sequence = readNumberLoose(row, 'index', 'sequence', 'sort', 'order_index') ?? fallbackSequence
  const orderNo = formatOrderNo(order)
  const address = readString(row, 'address', 'full_address', 'service_address')
  if (!orderNo && !address && !id) return null

  const position = readPointDeep(row) ?? { lat: 0, lng: 0 }

  return {
    id,
    category: 'essential' as TasksMapCategoryKey,
    address,
    orderId: orderNo || id,
    time: formatOrderTime(order),
    position,
    sequence,
  }
}

function buildStaffMarkerMap(markers: TasksMapBoardMarker[] | undefined): Map<string, TasksMapPoint> {
  const map = new Map<string, TasksMapPoint>()
  for (const marker of markers ?? []) {
    const row = marker as Record<string, unknown>
    const staffId = readString(row, 'staff_id', 'id')
    const point = readPointDeep(row)
    if (!staffId || !point || !isValidMapPoint(point)) continue
    map.set(staffId, point)
  }
  return map
}

function buildOrderMarkerLookup(
  markers: TasksMapBoardMarker[] | undefined,
): Map<string, Map<number, TasksMapBoardMarker>> {
  const lookup = new Map<string, Map<number, TasksMapBoardMarker>>()
  for (const marker of markers ?? []) {
    const row = marker as Record<string, unknown>
    // 订单标记必须用 staff_id；勿回退到 id（那常常是订单 id）
    const staffId = readString(row, 'staff_id')
    const index = readNumberLoose(row, 'index', 'sequence', 'sort', 'order_index')
    if (!staffId || index === null) continue
    const staffMarkers = lookup.get(staffId) ?? new Map<number, TasksMapBoardMarker>()
    staffMarkers.set(index, marker)
    lookup.set(staffId, staffMarkers)
  }
  return lookup
}

/** 选中人员时，后端可能省略 order_markers.staff_id，按 index / order_id 兜底 */
function listOrphanOrderMarkers(markers: TasksMapBoardMarker[] | undefined): TasksMapBoardMarker[] {
  return (markers ?? []).filter((marker) => {
    const row = marker as Record<string, unknown>
    return !readString(row, 'staff_id')
  })
}

function enrichOrderFromMarker(
  order: TasksMapPerson['orders'][number],
  marker?: TasksMapBoardMarker | TasksMapBoardRoutePoint,
): TasksMapPerson['orders'][number] {
  if (!marker) return order
  const row = marker as Record<string, unknown>
  const point = readPointDeep(row)
  const address = readString(row, 'address', 'full_address')
  const orderNo = readString(row, 'order_id', 'order_no')

  return {
    ...order,
    address: order.address || address,
    orderId: order.orderId || orderNo,
    position: isValidMapPoint(order.position)
      ? order.position
      : point && isValidMapPoint(point)
        ? point
        : order.position,
  }
}

function findMarkerForOrder(
  order: TasksMapPerson['orders'][number],
  markers: Array<TasksMapBoardMarker | TasksMapBoardRoutePoint>,
): TasksMapBoardMarker | TasksMapBoardRoutePoint | undefined {
  const byOrderId = markers.find((marker) => {
    const row = marker as Record<string, unknown>
    const markerOrderId = readString(row, 'order_id', 'order_no', 'id')
    return Boolean(
      markerOrderId &&
        (markerOrderId === order.orderId ||
          markerOrderId === order.id ||
          markerOrderId.replace(/^#/, '') === order.orderId.replace(/^#/, '')),
    )
  })
  if (byOrderId) return byOrderId

  return markers.find((marker) => {
    const row = marker as Record<string, unknown>
    const index = readNumberLoose(row, 'index', 'sequence', 'sort', 'order_index')
    return index === order.sequence
  })
}

function enrichOrdersWithMarkers(
  orders: TasksMapPerson['orders'],
  markers: Array<TasksMapBoardMarker | TasksMapBoardRoutePoint>,
): TasksMapPerson['orders'] {
  if (!markers.length) return orders
  return orders.map((order) => enrichOrderFromMarker(order, findMarkerForOrder(order, markers)))
}

function mapBoardStaff(
  staff: TasksMapBoardStaff,
  markerMap: Map<string, TasksMapPoint>,
  orderMarkerLookup: Map<string, Map<number, TasksMapBoardMarker>>,
  fallbackIndex: number,
): TasksMapPerson | null {
  const row = staff as Record<string, unknown>
  const id = readString(row, 'id')
  const name = readString(row, 'name')
  if (!id || !name) return null

  const staffOrderMarkers = orderMarkerLookup.get(id)
  const orders = (staff.orders ?? [])
    .map((order, orderIndex) => {
      const mapped = mapBoardOrder(order, orderIndex + 1)
      if (!mapped) return null
      const marker =
        staffOrderMarkers?.get(mapped.sequence) ??
        staffOrderMarkers?.get(orderIndex + 1) ??
        staffOrderMarkers?.get(orderIndex)
      return enrichOrderFromMarker(mapped, marker)
    })
    .filter((order): order is TasksMapPerson['orders'][number] => order != null)
    .sort((a, b) => a.sequence - b.sequence)

  const markerPosition = markerMap.get(id)
  const staffSelfPosition = readPointDeep(row)
  const orderPosition = orders.map((order) => order.position).find((point) => isValidMapPoint(point))
  const resolved =
    (markerPosition && isValidMapPoint(markerPosition) ? markerPosition : null) ??
    (staffSelfPosition && isValidMapPoint(staffSelfPosition) ? staffSelfPosition : null) ??
    orderPosition ??
    null
  const position = resolved ?? fallbackPositionForIndex(fallbackIndex)

  return {
    id,
    name,
    initials: readString(row, 'initials'),
    orderCount: readNumber(row, 'order_count') ?? orders.length,
    position,
    orders,
    color: '',
  }
}

function mapRoutePoints(points: TasksMapBoardRoutePoint[] | undefined): TasksMapPoint[] {
  return (points ?? [])
    .map((point) => readPointDeep(point as Record<string, unknown>))
    .filter((point): point is TasksMapPoint => isValidMapPoint(point))
}

function extractDriverRoutes(data: TasksMapBoardData | null | undefined): TasksMapBoardRoutePoint[] {
  const map = data?.map
  if (Array.isArray(map?.driver_routes) && map.driver_routes.length) return map.driver_routes
  if (Array.isArray(map?.selected_route) && map.selected_route.length) return map.selected_route
  return []
}

function enrichSelectedPersonOrders(
  people: TasksMapPerson[],
  selectedStaffId: number,
  data: TasksMapBoardData | null | undefined,
): TasksMapPerson[] {
  if (!selectedStaffId) return people
  const selectedId = String(selectedStaffId)
  const orphanMarkers = listOrphanOrderMarkers(data?.map?.order_markers)
  const routePoints = extractDriverRoutes(data)
  const selectedStaffMarkers = (data?.map?.order_markers ?? []).filter((marker) => {
    const row = marker as Record<string, unknown>
    return readString(row, 'staff_id') === selectedId
  })

  return people.map((person) => {
    if (person.id !== selectedId) return person
    let orders = enrichOrdersWithMarkers(person.orders, selectedStaffMarkers)
    orders = enrichOrdersWithMarkers(orders, orphanMarkers)
    orders = enrichOrdersWithMarkers(orders, routePoints)

    const orderPosition = orders.map((order) => order.position).find((point) => isValidMapPoint(point))
    return {
      ...person,
      position: isValidMapPoint(person.position) ? person.position : orderPosition ?? person.position,
      orders,
      orderCount: person.orderCount || orders.length,
    }
  })
}

export function mapTasksMapBoardToView(data: TasksMapBoardData | null | undefined): TasksMapBoardView {
  const markerMap = buildStaffMarkerMap(data?.map?.staff_markers)
  const orderMarkerLookup = buildOrderMarkerLookup(data?.map?.order_markers)
  const selectedStaffId =
    typeof data?.selected_staff_id === 'number' && Number.isFinite(data.selected_staff_id)
      ? data.selected_staff_id
      : 0
  const people = enrichSelectedPersonOrders(
    (data?.staff_list ?? [])
      .map((staff, index) => mapBoardStaff(staff, markerMap, orderMarkerLookup, index))
      .filter((person): person is TasksMapPerson => person != null),
    selectedStaffId,
    data,
  )

  const selectedRoute = mapRoutePoints(extractDriverRoutes(data))
  // 后端未给 driver_routes 时，用选中人员订单坐标拼路线，保证真实数据也能连线
  const selectedPerson = selectedStaffId
    ? people.find((person) => person.id === String(selectedStaffId))
    : undefined
  const routeFromOrders =
    selectedPerson?.orders
      .filter((order) => isValidMapPoint(order.position))
      .sort((a, b) => a.sequence - b.sequence)
      .map((order) => order.position) ?? []

  return {
    date: data?.date?.trim() ?? '',
    tab: data?.tab?.trim() ?? '',
    selectedStaffId,
    people: normalizeTasksMapPeople(people),
    selectedRoute: selectedRoute.length ? selectedRoute : routeFromOrders,
  }
}

/** POST `/api/admin/tasks-map/board` */
export async function postTasksMapBoard(
  params: TasksMapBoardParams,
): Promise<ApiResponse<TasksMapBoardData>> {
  if (shouldUseTasksMapMock()) {
    return getMockTasksMapBoardResponse(params) as ApiResponse<TasksMapBoardData>
  }

  const fallbackDates = buildTasksMapBoardFallbackDates(params.date)
  let lastResponse: ApiResponse<TasksMapBoardData> | null = null

  for (const date of fallbackDates) {
    const response = await requestTasksMapBoardOnce({ ...params, date })
    lastResponse = response
    if (isTasksMapBoardSuccess(response)) {
      return response
    }
    if (!isTasksMapBoardRetryableError(response)) {
      break
    }
  }

  if (shouldUseTasksMapFallbackMock()) {
    console.warn(
      '[tasks-map] Backend board API failed, using local mock data:',
      readTasksMapApiMessage(lastResponse),
    )
    return getMockTasksMapBoardResponse(params) as ApiResponse<TasksMapBoardData>
  }

  return lastResponse ?? {
    code: 500,
    msg: 'Failed to load tasks map board.',
    data: {} as TasksMapBoardData,
  }
}
